import { useCallback, useEffect, useRef, useState } from "react"
import useApi from "./useApi.js"

const POLL_MS = 3000
const ACTIVE_SHOP = new Set([ "pending", "crawled", "onboarding" ])

// Fetches GET /api/v1/shop (profile embedded) and re-polls every 3s while the
// shop is mid-onboarding. One request per poll (profile is embedded).
//
// Gate drives off `shop.status` (the lifecycle signal) and bails only on
// `profile.crawl_status === "failed"` (hard dead-end). We DO NOT gate on
// "profile exists" or "profile.crawl_status === 'crawling'" because both
// are per-job phase signals that race with the browser multiple times:
//
//   phase 1 · DiscoverPagesJob starting: profile may be null OR
//             crawl_status="pending" (schema default) for the first 100–500ms
//             under Sidekiq, before the job's update! flips it to "crawling"
//   phase 2 · crawled + review/conversion steps: profile.crawl_status sits
//             at "completed" while the owner clicks through review screens
//   phase 3 · AnalyzeProfileJob starting: same race as phase 1 but this time
//             profile already exists at "completed" and the job flips it back
//             to "crawling" on its first write
//
// An IS-allowlist on "crawling" fails closed in phases 1 and 3 (race window)
// and is a no-op in phase 2 (user is clicking anyway). A TERMINAL-gate on
// "completed/failed" fails closed in phase 3 because the previous job's
// "completed" outlasts the current job's startup race. The robust signal is
// shop.status — it advances monotonically through pending → crawled →
// onboarding → active and doesn't snap back between jobs. Polling during idle
// "crawled" is a 3s GET to an embedded-profile endpoint; cheap, and it makes
// the Continue-clicks-into-next-job transition zero-race.
export default function useShop() {
  const api = useApi()
  const [state, setState] = useState({ status: "loading", shop: null, profile: null, error: null })
  const timerRef = useRef(null)
  const mountedRef = useRef(true)

  const fetchOnce = useCallback(async () => {
    try {
      const shopPayload = await api.get("/api/v1/shop")
      if (!mountedRef.current) return
      const { profile, ...shop } = shopPayload
      setState({ status: "loaded", shop, profile: profile || null, error: null })
    } catch (err) {
      if (!mountedRef.current) return
      if (/^404/.test(err.message)) {
        setState({ status: "loaded", shop: null, profile: null, error: null })
      } else {
        setState((prev) => ({ ...prev, status: "error", error: err.message }))
      }
    }
  }, [ api ])

  useEffect(() => {
    mountedRef.current = true
    fetchOnce()
    return () => { mountedRef.current = false }
  }, [ fetchOnce ])

  useEffect(() => {
    const shop = state.shop
    const jobInFlight =
      shop && ACTIVE_SHOP.has(shop.status) &&
      state.profile?.crawl_status !== "failed"
    const awaitingInstall =
      shop && shop.status === "active" && !shop.script_installed
    const shouldPoll = jobInFlight || awaitingInstall

    if (!shouldPoll) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
      return
    }

    if (timerRef.current) return
    timerRef.current = setInterval(fetchOnce, POLL_MS)
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    }
  }, [ state.shop, state.profile, fetchOnce ])

  return { ...state, refresh: fetchOnce }
}
